import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import DateRangeFilter, { rangeFor, defaultGranularity } from "@/components/DateRangeFilter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const tooltipStyle = { backgroundColor: "#111827", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 };
const TICK = { fontSize: 10, fill: "#9CA3AF" };
const LEGEND_STYLE = { fontSize: 11 };
const COLORS = ["#DC2626", "#2563EB", "#16A34A", "#D97706", "#7C3AED", "#0891B2", "#DB2777", "#65A30D", "#EA580C", "#4B5563"];

const GROUPS = [
  { value: "department", label: "Bölüm" },
  { value: "machine", label: "Makine" },
  { value: "employee", label: "Personel" },
  { value: "product", label: "Ürün" },
];

const METRICS = [
  { value: "efficiency", label: "Verimlilik %" },
  { value: "actual", label: "Gerçek Üretim" },
  { value: "standard", label: "Standart Kapasite" },
  { value: "plan", label: "Plan" },
];

export default function Trends() {
  const [range, setRange] = useState(() => rangeFor({ key: "1m", months: 1 }));
  const [group, setGroup] = useState("department");
  const [metric, setMetric] = useState("efficiency");
  const [granularity, setGranularity] = useState("");
  const [selected, setSelected] = useState([]);
  const [meta, setMeta] = useState({ departments: [], machines: [], employees: [], products: [] });
  const [data, setData] = useState({ labels: [], series: [] });

  useEffect(() => { api.get("/meta/options").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const options = {
    department: meta.departments.map((d) => ({ id: d.code, label: d.name })),
    machine: meta.machines.map((m) => ({ id: m.code, label: `${m.code} — ${m.name}` })),
    employee: meta.employees.map((e) => ({ id: e.code, label: e.name })),
    product: meta.products.map((p) => ({ id: p.code, label: `${p.code} — ${p.name}` })),
  }[group] || [];

  useEffect(() => {
    const g = granularity || defaultGranularity(range.start, range.end);
    const ids = selected.length ? `&ids=${selected.join(",")}` : "";
    api.get(`/analytics/trend?start=${range.start}&end=${range.end}&granularity=${g}&group=${group}${ids}`)
      .then((r) => setData(r.data)).catch(() => {});
  }, [range, group, selected, granularity]);

  useEffect(() => { setSelected([]); }, [group]);

  const labels = data.labels || [];
  const chartData = labels.map((lb, i) => {
    const row = { name: lb };
    data.series?.forEach((s) => { row[s.name] = s.data[i]?.[metric]; });
    return row;
  });

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div data-testid="trends-page" className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Trend Analizi & Karşılaştırma</h1>
        <p className="text-sm text-muted-foreground mt-1">Bölüm, makine, personel ve ürün bazında karşılaştırmalı trendler</p>
      </div>

      <Card className="p-4 bg-card border-border space-y-3">
        <DateRangeFilter value={range} onChange={setRange} testidPrefix="trends-date" />
        <div className="flex flex-wrap gap-3 items-center">
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="trends-group-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GROUPS.map((g) => <SelectItem key={g.value} value={g.value} data-testid={`trends-group-${g.value}`}>{g.label} Karşılaştırma</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="trends-metric-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {METRICS.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`trends-metric-${m.value}`}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={granularity || "auto"} onValueChange={(v) => setGranularity(v === "auto" ? "" : v)}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="trends-granularity-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto" data-testid="trends-granularity-auto">Otomatik Çözünürlük</SelectItem>
              <SelectItem value="day" data-testid="trends-granularity-day">Günlük</SelectItem>
              <SelectItem value="week" data-testid="trends-granularity-week">Haftalık</SelectItem>
              <SelectItem value="month" data-testid="trends-granularity-month">Aylık</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-2" data-testid="trends-entity-picker">
          {options.map((o) => (
            <label key={o.id} className="flex items-center gap-1.5 text-xs cursor-pointer text-muted-foreground hover:text-foreground transition-colors duration-150">
              <Checkbox checked={selected.includes(o.id)} onCheckedChange={() => toggle(o.id)}
                data-testid={`trends-entity-${o.id}`} className="border-border" />
              {o.label}
            </label>
          ))}
          {options.length > 0 && <span className="text-[10px] text-muted-foreground/60 self-center">Seçim yoksa tümü gösterilir</span>}
        </div>
      </Card>

      <Card className="p-4 bg-card border-border" data-testid="trends-chart-card">
        <h3 className="text-lg font-medium mb-3">
          {GROUPS.find((g) => g.value === group)?.label} — {METRICS.find((m) => m.value === metric)?.label} Trendi
        </h3>
        <ResponsiveContainer width="100%" height={380}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
            <XAxis dataKey="name" tick={TICK} />
            <YAxis tick={TICK} domain={metric === "efficiency" ? [0, 120] : undefined} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => metric === "efficiency" ? `%${v}` : v} />
            <Legend wrapperStyle={LEGEND_STYLE} />
            {data.series?.map((s, i) => (
              <Line key={s.name} dataKey={s.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2}
                dot={false} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
